// src/components/Profile.js
import React, { useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { Separator } from "./ui/separator";
import { Shield, Building, Users, RefreshCw } from "lucide-react";
import { getApiUrl } from "../config/api";
import { useUser } from "../contexts/UserContext";

const Profile = () => {
  const { user, isAuthenticated, getAccessTokenSilently } = useAuth0();
  const {
    userProfile,
    refreshProfile,
    isLoading: profileLoading,
    primaryRole,
  } = useUser();
  const [userMetadata, setUserMetadata] = useState(null);
  const [organizationError, setOrganizationError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const createOrganization = async () => {
    setIsLoading(true);
    setOrganizationError(null);
    try {
      // Get the access token
      const accessToken = await getAccessTokenSilently();

      // Example organization data
      const organizationData = {
        name: "Test Organization",
        description: "This is a test organization",
      };

      // Make the API call to create organization
      const response = await fetch(getApiUrl("organization"), {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(organizationData),
      });

      const data = await response.json();
      setUserMetadata(data);
      setOrganizationError(null);
    } catch (error) {
      console.error("Error creating organization:", error);
      setOrganizationError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const getRoleDisplayName = (role) => {
    switch (role) {
      case "org_admin":
        return "Organization Admin";
      case "business_unit_head":
        return "Business Unit Head";
      case "business_user":
        return "Business User";
      default:
        return "No Role Assigned";
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case "org_admin":
        return <Shield className="h-4 w-4" />;
      case "business_unit_head":
        return <Building className="h-4 w-4" />;
      case "business_user":
        return <Users className="h-4 w-4" />;
      default:
        return null;
    }
  };

  const getRoleVariant = (role) => {
    switch (role) {
      case "org_admin":
        return "destructive";
      case "business_unit_head":
        return "default";
      case "business_user":
        return "secondary";
      default:
        return "outline";
    }
  };

  return (
    isAuthenticated && (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto space-y-6">
          {/* User Profile Card */}
          <Card>
            <CardHeader className="text-center">
              <div className="flex justify-center mb-4">
                <img
                  src={user.picture}
                  alt={user.name}
                  className="w-24 h-24 rounded-full"
                />
              </div>
              <CardTitle className="text-center">
                {userProfile?.name || user.name}
              </CardTitle>
              <CardDescription className="text-center">
                {userProfile?.email || user.email}
              </CardDescription>
              {userProfile?.title && (
                <p className="text-body-small text-muted-foreground">
                  {userProfile.title}
                </p>
              )}
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-center">
                <Badge
                  variant={getRoleVariant(primaryRole)}
                  className="flex items-center gap-2"
                >
                  {getRoleIcon(primaryRole)}
                  {getRoleDisplayName(primaryRole)}
                </Badge>
              </div>

              <Button
                onClick={refreshProfile}
                disabled={profileLoading}
                variant="outline"
                className="w-full flex items-center gap-2"
              >
                <RefreshCw
                  className={`h-4 w-4 ${profileLoading ? "animate-spin" : ""}`}
                />
                {profileLoading ? "Refreshing..." : "Refresh Profile"}
              </Button>
            </CardContent>
          </Card>

          {/* Role Details Card */}
          {userProfile && userProfile.permissions && (
            <Card>
              <CardHeader>
                <CardTitle>Role Details</CardTitle>
                <CardDescription>
                  Your current roles and permissions in the system
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Organizations */}
                {userProfile.permissions.organizations?.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-2 flex items-center gap-2">
                      <Shield className="h-4 w-4" />
                      Organization Admin
                    </h4>
                    <div className="space-y-2">
                      {userProfile.permissions.organizations.map((org) => (
                        <div
                          key={org.id}
                          className="flex items-center justify-between p-2 bg-muted rounded"
                        >
                          <span className="text-body-small">{org.name}</span>
                          <Badge variant="destructive" className="text-caption">
                            Admin
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Business Unit Head */}
                {userProfile.permissions.businessUnitHeadOf?.length > 0 && (
                  <>
                    <Separator />
                    <div>
                      <h4 className="font-medium mb-2 flex items-center gap-2">
                        <Building className="h-4 w-4" />
                        Business Unit Head
                      </h4>
                      <div className="space-y-2">
                        {userProfile.permissions.businessUnitHeadOf.map(
                          (bu) => (
                            <div
                              key={bu.id}
                              className="flex items-center justify-between p-2 bg-muted rounded"
                            >
                              <div>
                                <div className="text-body-small">{bu.name}</div>
                                <div className="text-caption text-muted-foreground">
                                  {bu.organization_name}
                                </div>
                              </div>
                              <Badge variant="default" className="text-caption">
                                Head
                              </Badge>
                            </div>
                          )
                        )}
                      </div>
                    </div>
                  </>
                )}

                {/* Business User */}
                {userProfile.permissions.businessUnits?.length > 0 && (
                  <>
                    <Separator />
                    <div>
                      <h4 className="font-medium mb-2 flex items-center gap-2">
                        <Users className="h-4 w-4" />
                        Business User
                      </h4>
                      <div className="space-y-2">
                        {userProfile.permissions.businessUnits.map((bu) => (
                          <div
                            key={bu.id}
                            className="flex items-center justify-between p-2 bg-muted rounded"
                          >
                            <div>
                              <div className="text-body-small">{bu.name}</div>
                              <div className="text-caption text-muted-foreground">
                                {bu.organization_name}
                              </div>
                            </div>
                            <Badge variant="secondary" className="text-caption">
                              User
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          )}

          {/* Debug Information Card */}
          {(userMetadata || organizationError) && (
            <Card>
              <CardHeader>
                <CardTitle>Debug Information</CardTitle>
              </CardHeader>
              <CardContent>
                {organizationError && (
                  <p className="text-destructive text-body-small mb-2">
                    Error: {organizationError}
                  </p>
                )}
                {userMetadata && (
                  <pre className="text-caption bg-muted p-2 rounded overflow-auto">
                    {JSON.stringify(userMetadata, null, 2)}
                  </pre>
                )}
              </CardContent>
            </Card>
          )}

          {/* Test Organization Creation */}
          <Card>
            <CardHeader>
              <CardTitle>Test Organization Creation</CardTitle>
              <CardDescription>
                This is a temporary feature for testing organization creation
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                onClick={createOrganization}
                disabled={isLoading}
                className="w-full"
              >
                {isLoading ? "Creating..." : "Create Test Organization"}
              </Button>
            </CardContent>
          </Card>

          {/* Raw User Data Card */}
          <Card>
            <CardHeader>
              <CardTitle>Raw User Data</CardTitle>
              <CardDescription>
                This shows the raw data from Auth0 and our user service
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-medium mb-2">Auth0 User Object:</h4>
                <pre className="text-caption bg-muted p-2 rounded overflow-auto">
                  {JSON.stringify(user, null, 2)}
                </pre>
              </div>
              <Separator />
              <div>
                <h4 className="font-medium mb-2">User Profile from Backend:</h4>
                <pre className="text-caption bg-muted p-2 rounded overflow-auto">
                  {JSON.stringify(userProfile, null, 2)}
                </pre>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  );
};

export default Profile;
